import {
  openAuthPopup,
  POPUP_AUTH_CALLBACK_QUERY_PARAM_NAME,
} from "./popupAuth.js";
import { getSupabaseClient } from "./supabaseClient.js";

export type OAuthProvider = "google" | "azure";

/**
 * Starts the Supabase OAuth sign-in flow for Google or Microsoft. In popup mode
 * (embedded iframe auth, e.g. Fountain) the provider page opens in a popup that
 * posts the auth code back to the opener; otherwise the browser redirects to
 * the provider.
 */
export async function startOAuthSignIn(
  provider: OAuthProvider,
  { usePopup }: { usePopup: boolean },
): Promise<{ error: Error | null }> {
  const redirectTo = new URL(window.location.href);
  // Clear the hash to avoid issues with the OAuth callback URL.
  redirectTo.hash = "";
  // If we are doing popup auth, include a flag in the callback URL so the popup knows to send the code back to the parent window
  if (usePopup) {
    redirectTo.searchParams.set(POPUP_AUTH_CALLBACK_QUERY_PARAM_NAME, "true");
  }

  const { data, error } = await getSupabaseClient().auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: redirectTo.toString(),
      queryParams: {
        // Ensures that user is always able to choose the account they want to log in with
        prompt: "select_account",
      },
      // Don't redirect to OAuth signin page if we are using popup auth (we need to open it in a popup instead)
      skipBrowserRedirect: usePopup,
      ...(provider === "azure" && { scopes: "email" }),
    },
  });

  if (error) {
    return { error };
  }

  // If embedded and we have a URL, open in popup window
  if (usePopup && data.url) {
    openAuthPopup(data.url);
  }

  return { error: null };
}
